import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ComplianceCoverageCard } from "@/components/ComplianceFrameworksDisplay";
import { 
  Shield, ChevronRight, ChevronLeft, Loader2, DollarSign, Globe, ShieldCheck,
  CheckCircle2, AlertTriangle, XCircle, Sparkles, RefreshCw, ChevronDown, 
  ArrowRight, Wrench, FileWarning
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Phase2Audit = ({ project, onUpdateProject, onNext, onBack, onNavigateToTab }) => {
  const { token } = useAuth();
  const [auditing, setAuditing] = useState(false);
  const [auditResults, setAuditResults] = useState(null);
  const [currentAudit, setCurrentAudit] = useState("");
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (project?.audit_results) {
      setAuditResults(project.audit_results);
    } else {
      runAudits();
    }
  }, [project?.id]);

  const runAudits = async () => {
    try {
      setAuditing(true);
      setProgress(0);
      
      // Simulate audit progression
      setCurrentAudit("FinOps Cost Auditor");
      setProgress(10);
      await new Promise(r => setTimeout(r, 500));
      setProgress(30);
      
      setCurrentAudit("Global Resilience Audit");
      await new Promise(r => setTimeout(r, 500));
      setProgress(50);
      
      setCurrentAudit("Compliance Gate");
      await new Promise(r => setTimeout(r, 500));
      setProgress(70);
      
      // Call API for actual audit
      const response = await axios.post(`${API}/ai/audit`, {
        project_id: project.id
      }, { 
        timeout: 180000,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      setProgress(100);
      setAuditResults(response.data);
      onUpdateProject({
        ...project,
        audit_results: response.data,
        current_phase: 2
      });
      toast.success("Audits completed!");
    } catch (err) {
      let errorMsg = "Audit failed. Please try again.";
      if (err.code === 'ECONNABORTED') {
        errorMsg = "Request timed out. Please try again.";
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      toast.error(errorMsg);
    } finally {
      setAuditing(false);
      setCurrentAudit("");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'fail': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <CheckCircle2 className="w-5 h-5 text-zinc-500" />;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 60) return "text-yellow-500";
    return "text-red-500";
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-6 md:mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-sm bg-[#7000ff]/10 border border-[#7000ff]/30 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#7000ff]" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Phase 2: Triple-Audit Suite</h1>
            <p className="text-sm md:text-base text-zinc-500">Automated FinOps, Resilience, and Compliance validation</p>
          </div>
        </div>
      </div>

      {auditing ? (
        <Card className="bg-[#09090b] border-white/5">
          <CardContent className="py-16 md:py-20 flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-[#7000ff]/10 border border-[#7000ff]/30 flex items-center justify-center mb-6">
              <Loader2 className="w-8 h-8 text-[#7000ff] animate-spin" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Running Audits</h3>
            <p className="text-zinc-500 text-center mb-6">{currentAudit || "Initializing..."}</p>
            <div className="w-full max-w-md">
              <Progress value={progress} className="h-2" />
            </div>
          </CardContent>
        </Card>
      ) : !auditResults ? (
        <Card className="bg-[#09090b] border-white/5">
          <CardContent className="py-16 flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-sm bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-6">
              <Shield className="w-8 h-8 text-zinc-600" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">No Audit Results</h3>
            <p className="text-zinc-500 mb-6">Run the Triple-Audit to validate your architecture</p>
            <Button
              onClick={runAudits}
              className="bg-[#7000ff] text-white hover:bg-[#7000ff]/90 rounded-sm"
              data-testid="run-audits-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Run Audits
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Overall Score */}
          <Card className="bg-[#09090b] border-white/5">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <h3 className="text-lg font-semibold text-white mb-1">Overall Resilience Score</h3>
                  <p className="text-sm text-zinc-500">Composite score from all audits</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className={`text-5xl md:text-6xl font-black ${getScoreColor(auditResults.resilience_score || 0)}`}>
                    {auditResults.resilience_score || 0}
                  </div>
                  <div className="text-zinc-500 text-lg">/100</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Audit Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {/* FinOps Audit */}
            <Card className="bg-[#09090b] border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <div className="w-8 h-8 rounded-sm bg-green-500/10 flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-green-400" />
                  </div>
                  FinOps Cost Auditor
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Budget Status</span>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(auditResults.finops?.budget_status === 'PASS' ? 'pass' : 'fail')}
                    <span className={auditResults.finops?.budget_status === 'PASS' ? 'text-green-500' : 'text-red-500'}>
                      {auditResults.finops?.budget_status || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Estimated Cost</span>
                  <span className="text-white font-semibold">{auditResults.finops?.estimated_cost || '$0'}/mo</span>
                </div>
                <div className="border-t border-white/5 pt-3">
                  <p className="text-xs text-zinc-500 mb-2">Optimization Tips:</p>
                  <ul className="space-y-1">
                    {(auditResults.finops?.tips || []).slice(0, 3).map((tip, idx) => (
                      <li key={idx} className="text-xs text-zinc-400 flex items-start gap-2">
                        <span className="text-green-500">•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Resilience Audit */}
            <Card className="bg-[#09090b] border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <div className="w-8 h-8 rounded-sm bg-cyan-500/10 flex items-center justify-center">
                    <Globe className="w-4 h-4 text-cyan-400" />
                  </div>
                  Global Resilience Audit
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Multi-AZ</span>
                  {getStatusIcon(auditResults.resilience?.multi_az ? 'pass' : 'warning')}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Auto-Scaling</span>
                  {getStatusIcon(auditResults.resilience?.auto_scaling ? 'pass' : 'warning')}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Backup Strategy</span>
                  {getStatusIcon(auditResults.resilience?.backup ? 'pass' : 'warning')}
                </div>
                <div className="border-t border-white/5 pt-3">
                  <p className="text-xs text-zinc-500 mb-2">SPOF Detected:</p>
                  <ul className="space-y-1">
                    {(auditResults.resilience?.spof || ['None detected']).map((spof, idx) => (
                      <li key={idx} className="text-xs text-zinc-400 flex items-start gap-2">
                        <span className="text-yellow-500">⚠</span>
                        <span>{spof}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Compliance Gate */}
            <Card className="bg-[#09090b] border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <div className="w-8 h-8 rounded-sm bg-purple-500/10 flex items-center justify-center">
                    <ShieldCheck className="w-4 h-4 text-purple-400" />
                  </div>
                  Compliance Gate
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Encryption at Rest</span>
                  {getStatusIcon(auditResults.compliance?.encryption_at_rest ? 'pass' : 'fail')}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">Private Networking</span>
                  {getStatusIcon(auditResults.compliance?.private_networking ? 'pass' : 'warning')}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-400">IAM Least Privilege</span>
                  {getStatusIcon(auditResults.compliance?.iam_least_privilege ? 'pass' : 'warning')}
                </div>
                <div className="border-t border-white/5 pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-500">Critical Issues</span>
                    <span className={`text-sm font-semibold ${
                      (auditResults.compliance?.critical_count || 0) > 0 ? 'text-red-500' : 'text-green-500'
                    }`}>
                      {auditResults.compliance?.critical_count || 0}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* DRAFT: Enhanced Compliance Coverage Card */}
          <div className="mt-6">
            <ComplianceCoverageCard
              selectedFrameworks={project?.phase1?.security?.compliance || []}
              validatedFrameworks={auditResults?.compliance?.validated_frameworks || []}
              complianceScore={auditResults?.compliance?.score || 0}
            />
          </div>

          {/* Prescriptive Remediation Section - Uses backend data */}
          {auditResults.remediation && auditResults.remediation.total_count > 0 && (
            <Card className="bg-gradient-to-r from-yellow-500/5 to-red-500/5 border-yellow-500/20">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-base flex items-center gap-2">
                    <FileWarning className="w-5 h-5 text-yellow-500" />
                    Prescriptive Remediation Guide
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {auditResults.remediation.critical_count > 0 && (
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                        {auditResults.remediation.critical_count} Critical
                      </Badge>
                    )}
                    {auditResults.remediation.high_count > 0 && (
                      <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                        {auditResults.remediation.high_count} High
                      </Badge>
                    )}
                    {auditResults.remediation.medium_count > 0 && (
                      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                        {auditResults.remediation.medium_count} Medium
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-zinc-400 mb-4">
                  To achieve a higher resilience score and pass all compliance checks, address the following issues in Phase 1:
                </p>
                <div className="space-y-3">
                  {auditResults.remediation.items.map((item, idx) => (
                    <RemediationItem 
                      key={item.id || idx}
                      item={item}
                      onFix={() => {
                        if (onNavigateToTab) {
                          onNavigateToTab(item.tab);
                          toast.info(`Navigate to "${item.section}" and ${item.action.toLowerCase()}`);
                        } else {
                          onBack();
                          toast.info(`Go to "${item.section}" tab and ${item.action.toLowerCase()}`);
                        }
                      }}
                    />
                  ))}
                </div>
                <div className="mt-4 flex justify-center">
                  <Button
                    variant="outline"
                    onClick={onBack}
                    className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                    data-testid="go-back-fix-btn"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Go Back to Fix All Issues
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Re-run Audit Button */}
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={runAudits}
              className="border-zinc-700 text-zinc-400 hover:bg-white/5"
              data-testid="rerun-audits-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Re-run Audits
            </Button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/5">
        <Button
          variant="outline"
          onClick={onBack}
          className="border-zinc-700 text-zinc-300 hover:bg-white/5 rounded-sm"
          data-testid="phase2-back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Intake
        </Button>

        <Button
          onClick={onNext}
          disabled={!auditResults}
          className="bg-[#00f0ff] text-black font-semibold hover:bg-[#00f0ff]/90 hover:shadow-[0_0_20px_rgba(0,240,255,0.4)] rounded-sm px-6"
          data-testid="phase2-next-btn"
        >
          Review Findings
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// Remediation Item Component with collapsible details
const RemediationItem = ({ item, onFix }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'critical':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs">Critical</Badge>;
      case 'high':
        return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">High</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs">Medium</Badge>;
      default:
        return <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30 text-xs">Info</Badge>;
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'medium':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-zinc-500" />;
    }
  };

  const getTabDisplayName = (tab) => {
    const tabNames = {
      'basic': 'Basic Info',
      'scale': 'Scale & Ops',
      'security': 'Security',
      'data': 'Data & DR',
      'budget': 'Budget & Stack'
    };
    return tabNames[tab] || tab;
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className={`bg-zinc-900/50 border rounded-lg overflow-hidden ${
        item.severity === 'critical' ? 'border-red-500/30' : 
        item.severity === 'high' ? 'border-orange-500/30' : 'border-zinc-800'
      }`}>
        <CollapsibleTrigger asChild>
          <div className="p-4 cursor-pointer hover:bg-white/5 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getSeverityIcon(item.severity)}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{item.title}</span>
                    {getSeverityBadge(item.severity)}
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">
                    Fix in: <span className="text-cyan-400">{getTabDisplayName(item.tab)}</span> → <span className="text-white">{item.section}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFix();
                  }}
                  className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 text-xs"
                  data-testid={`fix-${item.id}-btn`}
                >
                  <Wrench className="w-3 h-3 mr-1" />
                  Fix Now
                </Button>
                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </div>
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 border-t border-zinc-800">
            <div className="pt-4 space-y-3">
              <div>
                <p className="text-xs text-zinc-500 mb-1">Description</p>
                <p className="text-sm text-zinc-300">{item.description}</p>
              </div>
              {item.current_value && (
                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                  <p className="text-xs text-red-400 mb-1">Current Value</p>
                  <p className="text-sm text-white font-mono">{item.current_value}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-zinc-500 mb-1">Impact</p>
                <p className="text-sm text-zinc-400">{item.impact}</p>
              </div>
              <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3">
                <p className="text-xs text-cyan-400 mb-2 flex items-center gap-1">
                  <Wrench className="w-3 h-3" />
                  How to Fix
                </p>
                <p className="text-sm text-white mb-2">{item.action}</p>
                {item.exact_steps && item.exact_steps.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-cyan-500/10">
                    <p className="text-xs text-cyan-400 mb-2">Step-by-step:</p>
                    <ol className="space-y-1">
                      {item.exact_steps.map((step, idx) => (
                        <li key={idx} className="text-xs text-zinc-300 flex items-start gap-2">
                          <span className="text-cyan-400 font-mono text-xs min-w-[1.5rem]">{step.startsWith('Option') || step.startsWith('  ') ? '' : `${idx + 1}.`}</span>
                          <span className={step.startsWith('  ') ? 'pl-4' : ''}>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
              {item.compliance_frameworks && item.compliance_frameworks.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 mb-2">Affected Compliance Frameworks</p>
                  <div className="flex flex-wrap gap-1">
                    {item.compliance_frameworks.map((framework) => (
                      <Badge key={framework} variant="outline" className="text-xs border-zinc-700 text-zinc-400">
                        {framework}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
